import { JSXElementConstructor, ReactElement, ReactNode, useCallback, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router';
import { useSelector } from '../redux/modules/helpers/useSelector';
import { UserTypes } from '../../shared/constants/userTypes';

export type Tab = {
  value: string;
  to: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  name: string | ReactElement<any, string | JSXElementConstructor<any>>;
  headerTitle: ReactNode;
  requiresAuth?: boolean;
  requiresPPSMovies?: boolean;
  requiresPPSGalleries?: boolean;
  className?: string;
  activeClassName?: string;
  showCamsOnTab?: boolean;

  // @TODO slowly remove
  disabled?: boolean;
};

type TabRoute = {
  path: string | string[];
  value?: string;
  breadcrumb?: Function;
};

type UseTabs = (configPrefix: string, tabs: Tab[], tabRoutes: TabRoute[]) => [Tab[], Tab | null | undefined];

export const useTabs: UseTabs = (prefix, tabs, tabRoutes) => {
  const user = useSelector((state) => state.auth.user);
  const userType = useSelector((state) => state.auth.userType) === UserTypes.Guest ? 'guest' : 'member';
  const headers = useSelector((state) => state.app.config.tabSeoHeaders);
  const location = useLocation();

  const determineHeaderTitle = useCallback(
    (tab: Tab) => {
      const key = [prefix, tab.value.toLowerCase().replace(/\s/g, '-'), userType].join(':');
      return headers?.[key]?.header ?? tab.headerTitle;
    },
    [prefix, userType, headers],
  );

  const activeTabs = useMemo(
    () =>
      tabs
        .filter((tab) => (tab.requiresAuth ? !!user : true))
        .filter((tab) => !tab.disabled)
        .map((tab) => ({ ...tab, headerTitle: determineHeaderTitle(tab) })),
    [tabs, user, determineHeaderTitle],
  );

  const activeTab = useMemo(() => {
    const routeTab = tabRoutes.find((route) => {
      const result = !!matchPath(location.pathname, {
        path: route.path,
        exact: true,
      });
      return result;
    });

    if (!routeTab) return null;

    return activeTabs.find((tab) => routeTab.value === tab.value);
  }, [location.pathname, tabRoutes, activeTabs]);

  return [activeTabs, activeTab];
};
